import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import {
  inventoryCountService,
  InventoryCountItemsListResponse,
  InventoryCountItemSummary,
} from '../../../../../core/modules/inventoryCount/services/api';

type Phase = 'entering' | 'confirming' | 'done';

const speak = (text: string) => {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'es-CL';
  window.speechSynthesis.speak(utterance);
};

interface Props {
  companyId: number;
  sessionId: number;
}

const ManualCountingPanel = ({ companyId, sessionId }: Props) => {
  const navigate = useNavigate();

  const [data, setData] = useState<InventoryCountItemsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [inputValue, setInputValue] = useState('');
  const [phase, setPhase] = useState<Phase>('entering');

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await inventoryCountService.listSessionItems(companyId, sessionId, 'all');
      setData(result);
    } catch (err) {
      setError('No se pudo cargar el listado de productos a contar.');
    } finally {
      setLoading(false);
    }
  }, [companyId, sessionId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const pendingItems = useMemo(() => (data ? data.items.filter((i) => !i.isCounted) : []), [data]);
  const currentItem: InventoryCountItemSummary | undefined = pendingItems[0];

  const varianceInfo = useMemo(() => {
    if (!currentItem || inputValue.trim() === '') return null;
    const theoretical = Number(currentItem.theoreticalQty);
    const counted = Number(inputValue);
    const threshold = data ? Number(data.varianceThresholdPct) : 10;
    if (Number.isNaN(theoretical) || Number.isNaN(counted) || Number.isNaN(threshold)) return null;
    const deviationPct = theoretical === 0 ? (counted === 0 ? 0 : Infinity) : (Math.abs(counted - theoretical) / theoretical) * 100;
    return { deviationPct, threshold, exceeds: deviationPct > threshold };
  }, [currentItem, inputValue, data]);

  useEffect(() => {
    setInputValue('');
    setPhase('entering');
  }, [currentItem?.productId]);

  const handleGoToConfirm = () => {
    const parsed = Number(inputValue);
    if (inputValue.trim() === '' || Number.isNaN(parsed) || parsed < 0) {
      setError('Ingresa una cantidad válida.');
      return;
    }
    setError(null);
    speak(inputValue);
    setPhase('confirming');
  };

  const handleReplay = () => {
    speak(inputValue);
  };

  const handleCorrect = () => {
    setPhase('entering');
  };

  const handleFinalConfirm = async () => {
    if (!currentItem) return;
    setSubmitting(true);
    setError(null);
    try {
      await inventoryCountService.submitCount(companyId, sessionId, currentItem.productId, {
        countedQty: Number(inputValue),
        method: 'manual',
      });
      await loadItems();
    } catch (err) {
      setError('No se pudo registrar el conteo. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const percent = data?.percent ?? 0;
  const total = data?.total ?? 0;
  const counted = data?.counted ?? 0;

  return (
    <Box display="flex" flexDirection="column" alignItems="center" minHeight="60vh" px={3} py={4}>
      <Box sx={{ maxWidth: 480, width: '100%' }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <IconButton onClick={() => navigate('/dashboard/counting')} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="body2" color="text.secondary">
            {counted} de {total} SKUs contados
          </Typography>
        </Box>

        <LinearProgress variant="determinate" value={percent} sx={{ height: 8, borderRadius: 4, mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!currentItem ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 5 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                ¡Listo! 🎉
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contaste todos los SKUs de esta sesión ({total} de {total}).
              </Typography>
              <Button sx={{ mt: 3 }} variant="contained" onClick={() => navigate('/dashboard/counting')}>
                Volver al inicio
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent sx={{ py: 4 }}>
              <Typography variant="overline" color="text.secondary">
                SKU {currentItem.sku}
              </Typography>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {currentItem.productName}
              </Typography>
              {currentItem.location && (
                <Typography
                  variant="body2"
                  color="primary.main"
                  sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <LocationOnOutlinedIcon fontSize="small" /> {currentItem.location}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Cantidad teórica: <strong>{currentItem.theoreticalQty}</strong>
              </Typography>

              {phase === 'entering' && (
                <Stack spacing={2}>
                  <TextField
                    label="Cantidad contada"
                    type="number"
                    autoFocus
                    fullWidth
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    inputProps={{ min: 0, inputMode: 'numeric' }}
                  />
                  <Button variant="contained" size="large" onClick={handleGoToConfirm}>
                    Confirmar conteo
                  </Button>
                </Stack>
              )}

              {phase === 'confirming' && (
                <Stack spacing={2}>
                  <Alert
                    severity="info"
                    icon={<VolumeUpOutlinedIcon />}
                    action={
                      <Button size="small" onClick={handleReplay}>
                        Repetir
                      </Button>
                    }
                  >
                    ¿Escuchaste bien: <strong>{inputValue}</strong>?
                  </Alert>
                  {varianceInfo?.exceeds && (
                    <Alert severity="warning">
                      El conteo ingresado (<strong>{inputValue}</strong>) difiere del teórico (
                      <strong>{currentItem.theoreticalQty}</strong>) en{' '}
                      <strong>
                        {Number.isFinite(varianceInfo.deviationPct)
                          ? `${varianceInfo.deviationPct.toFixed(1)}%`
                          : 'más del 100%'}
                      </strong>
                      , por sobre el umbral configurado para esta sesión ({varianceInfo.threshold}%).
                      Verifica la cantidad antes de confirmar.
                    </Alert>
                  )}
                  <Stack direction="row" spacing={2}>
                    <Button variant="outlined" fullWidth onClick={handleCorrect} disabled={submitting}>
                      No, corregir
                    </Button>
                    <Button variant="contained" fullWidth onClick={handleFinalConfirm} disabled={submitting}>
                      {submitting ? <CircularProgress size={20} /> : 'Sí, correcto'}
                    </Button>
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default ManualCountingPanel;
